// Modal Styling Constants and Utilities for Order Summary Modals

// Color schemes for each modal type
export const MODAL_COLORS = {
  actual: {
    header: '#2c5f7c',
    highlight: '#fff3cd',
    highlightHover: '#ffe69c',
  },
  additional: {
    header: '#6b4c7a',
    highlight: '#fff3cd',
    highlightHover: '#ffe69c',
  },
  extraLoading: {
    header: '#2d7a5f',
    highlight: '#d4edda',
    highlightHover: '#c3e6cb',
  },
  sortOrder: {
    header: '#739072',
    highlight: '#fff3cd',
    highlightHover: '#ffe69c',
  },
};

// Button color schemes
export const BUTTON_COLORS = {
  excel: {
    backgroundColor: '#10a37f',
    color: 'white',
  },
  print: {
    backgroundColor: '#4a90e2',
    color: 'white',
  },
  close: {
    backgroundColor: '#6c757d',
    color: 'white',
  },
};

// Modal container styles
export const getModalWrapperStyles = () => ({
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  zIndex: 10,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
});

export const getModalContentStyles = () => ({
  width: '90%',
  maxWidth: '1400px',
  maxHeight: '92vh',
  marginTop: '20px',
  padding: '16px',
  backgroundColor: '#f0f7f4',
  borderRadius: '8px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  overflowY: 'auto',
});

// Button container and button styles
export const getButtonContainerStyles = () => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
  marginBottom: '10px',
});

export const getButtonStyles = (buttonType) => ({
  minWidth: '80px',
  height: '32px',
  borderRadius: '16px',
  fontWeight: 600,
  fontSize: '12px',
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  backgroundColor: BUTTON_COLORS[buttonType]?.backgroundColor || '#6c757d',
  color: BUTTON_COLORS[buttonType]?.color || 'white',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
});

// Modal title styles
export const getModalTitleStyles = () => ({
  fontFamily: '"Times New Roman"',

  fontSize: '16px',
  fontWeight: 500,
  textAlign: 'center',
  color: '#2c3e50',
  marginBottom: '12px',
  marginTop: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

// Table container styles
export const getTableContainerStyles = () => ({
  maxHeight: '68vh',
  overflowY: 'auto',
  overflowX: 'auto',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#888',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: '#555',
    },
  },
});

// Table styles
export const getTableStyles = () => ({
  minWidth: 650,
});

// Table header styles
export const getTableHeadStyles = (modalType) => ({
  position: 'sticky',
  top: 0,
  zIndex: 2,
  backgroundColor: MODAL_COLORS[modalType]?.header || '#2c5f7c',
});

export const getTableHeaderCellStyles = (alignment = 'left') => ({
  color: 'white',
  fontWeight: 500,
  fontSize: '16px',
  fontFamily: 'Times New Roman',
  padding: '8px 6px',
  borderRight: '1px solid rgba(255, 255, 255, 0.2)',
  borderBottom: '2px solid rgba(255, 255, 255, 0.3)',
  textAlign: alignment,
  whiteSpace: 'nowrap',
  '&:last-child': {
    borderRight: 'none',
  },
});

// Table body row styles
export const getTableRowStyles = (isHighlighted = false, modalType = 'actual') => {
  if (isHighlighted) {
    return {
      backgroundColor: `${MODAL_COLORS[modalType]?.highlight} !important`,
      fontWeight: 700,
      '&:hover': {
        backgroundColor: `${MODAL_COLORS[modalType]?.highlightHover} !important`,
      },
    };
  }

  return {
    '&:nth-of-type(even)': {
      backgroundColor: '#f8f9fa',
    },
    '&:nth-of-type(odd)': {
      backgroundColor: 'white',
    },
    '&:hover': {
      backgroundColor: '#e8f4f8',
      transition: 'background-color 0.2s ease',
    },
    borderBottom: '1px solid #dee2e6',
  };
};

// Table cell styles by content type
export const getTableCellStyles = (contentType = 'text', isEmphasized = false) => {
  const baseStyles = {
    fontSize: '14px',
    padding: '6px',
    color: isEmphasized ? '#ff6b6b' : '#2c3e50',
    verticalAlign: 'middle',
  };

  switch (contentType) {
    case 'numeric':
      return {
        ...baseStyles,
        textAlign: 'right',
        fontFamily: '"Times New Roman", Times, serif',
        fontWeight: isEmphasized ? 600 : 'normal',
      };
    case 'center':
      return {
        ...baseStyles,
        textAlign: 'center',
        fontFamily: '"Times New Roman", Times, serif',
        fontWeight: isEmphasized ? 600 : 'normal',
      };
    case 'text':
    default:
      return {
        ...baseStyles,
        textAlign: 'left',
        fontFamily: '"Times New Roman", Times, serif',
        fontWeight: isEmphasized ? 600 : 'normal',
      };
  }
};

// Empty state cell styles
export const getEmptyStateCellStyles = () => ({
  textAlign: 'center',
  padding: '40px',
  color: '#6c757d',
  fontSize: '14px',
});

// Utility function for conditional row highlighting
export const shouldHighlightRow = (item, modalType) => {
  switch (modalType) {
    case 'actual':
      // Highlight total rows or rows with specific markers
      return item.isTotal || item.CustomerName?.toLowerCase().includes('total');
    case 'additional':
      // Highlight "Total for" rows
      return item.CustomerName?.toLowerCase().includes('total for');
    case 'extraLoading':
      // Highlight GRAND TOTAL row
      return item.productCategory?.toUpperCase() === 'GRAND TOTAL';
    case 'sortOrder':
      // Highlight Grand Total row
      return item.ordertype?.toLowerCase() === 'grand total';
    default:
      return false;
  }
};

// Utility function to determine if a value should be displayed (for conditional rendering)
export const shouldShowValue = (currentItem, previousItem, fieldName) => {
  if (!previousItem) return true;
  return currentItem[fieldName] !== previousItem[fieldName];
};

// Utility function for formatting decimal values
export const formatDecimal = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') return '0.00';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return isNaN(numValue) ? '0.00' : numValue.toFixed(decimals);
};

// Utility function for formatting dates
export const formatDate = (dateValue) => {
  if (!dateValue) return '-';
  try {
    return new Date(dateValue).toLocaleDateString('en-GB');
  } catch (error) {
    return '-';
  }
};
